import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Renderer2,
  Self,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';
import { DateTimeAdapter, OwlDateTimeComponent } from '@danielmoncada/angular-datetime-picker';
import { PickerType } from '@danielmoncada/angular-datetime-picker/lib/date-time/date-time.class';
import moment, { Moment } from 'moment';
import { Subscription, filter } from 'rxjs';
import {
  Language,
  getCurrentTimeWithTimezone,
  markRequiredControlWithAsteriskSymbol,
  markAsTouchedAndUpdate,
  IconNames,
  DATEPICKER_DISPLAY_HOUR_ONLY_FORMAT,
} from '../../common';
import { DATEPICKER_DISPLAY_DATE_FORMAT, LOCALE_CODE_LIST } from '../../common/constants/date-config';
import { AutoUnsubscribe } from '../../common/decorators/auto-unsubscribe.decorator';
import { AppFacade } from '../../store';

@Component({
  selector: 'ui-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class DatepickerComponent implements OnInit, ControlValueAccessor {
  @ViewChild('datetimePicker') picker!: OwlDateTimeComponent<Moment>;
  @Input() label: string;
  @Input() min?: string | Date;
  @Input() max?: string | Date;
  @Input() disabled?: boolean;
  @Input() errorMessage?: string;
  @Input() fontIcon = IconNames.CalendarClock;
  @Input() testId?: string;
  @Input() pickerType: PickerType = 'both';
  @Input() showWarningIcon: boolean;
  @Input() warningTooltip: string;
  @Input() timezoneText?: string;
  @Input() changeHourOnly = false;

  @Input() set timezone(value: string) {
    if (value) {
      this.startTime = new Date(getCurrentTimeWithTimezone(value));
    }
  }

  @Input() set excludeDays(data: number[]) {
    if (data) {
      this.invalidDates = data;
    }
  }

  @Input() set isValidationTriggered(trigger: boolean) {
    if (trigger) {
      markAsTouchedAndUpdate(this.dateControl);
      this.cd.markForCheck();
    }
  }

  startTime: number | Moment | Date;
  dateControl = new UntypedFormControl();

  isDisabled: boolean;

  invalidDates: number[] = [];
  dateFormat = this.isHourOnlyMode ? DATEPICKER_DISPLAY_HOUR_ONLY_FORMAT : DATEPICKER_DISPLAY_DATE_FORMAT;
  iconNames = IconNames;
  subscriptions: Subscription[] = [];

  isFieldRequired: (control: UntypedFormControl | AbstractControl) => string = markRequiredControlWithAsteriskSymbol;

  constructor(
    @Self() public ngControl: NgControl,
    public dateTimeAdapter: DateTimeAdapter<Date>,
    private appFacade: AppFacade,
    private cd: ChangeDetectorRef,
    private renderer: Renderer2,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.setStartTimeForHourMode();
    this.setLocaleValueSubscription();
    this.initDateControlValueSubscription();
    this.initDateControlValidationSubscription();
  }

  registerOnChange(fn: () => number): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.dateControl.disable({ emitEvent: false }) : this.dateControl.enable({ emitEvent: false });

    this.isDisabled = isDisabled;
  }

  writeValue(dateTime: Date | Moment | number): void {
    if (this.isHourOnlyMode && typeof dateTime === 'number') {
      const hourMoment = moment().hour(dateTime).minute(0).second(0);

      this.startTime = hourMoment;

      this.dateControl.setValue(hourMoment, { emitEvent: false });

      return;
    }

    const dateTimeToMoment = dateTime ? moment(dateTime) : dateTime;

    if (dateTimeToMoment) {
      this.startTime = dateTimeToMoment;
    }

    this.dateControl.setValue(dateTimeToMoment, {
      emitEvent: false,
    });
  }

  onDateValueChanged(value: Moment): void {
    const containerElement = document.querySelector('.owl-dt-dialog');
    if (this.picker && containerElement) {
      this.renderer.addClass(containerElement, 'picker-reloading');
      setTimeout(() => {
        const startOfSelectedDay = value.clone().startOf('day');
        this.dateControl.setValue(startOfSelectedDay);
        this.picker.close();
        this.picker.open();
      }, 125);
    } else {
      const startOfSelectedDay = value.clone().startOf('day');
      this.dateControl.setValue(startOfSelectedDay);
      this.picker.close();
      setTimeout(() => this.picker.open(), 50);
    }
  }

  get isHourOnlyMode(): boolean {
    return this.pickerType === 'timer' && this.changeHourOnly;
  }

  private onChange: (value: number) => number;

  private onTouched: () => void;

  private initDateControlValueSubscription(): void {
    const subscription = this.dateControl.valueChanges.subscribe((value: Moment) => {
      if (value) {
        this.startTime = value;

        if (this.isHourOnlyMode) {
          this.onChange(value.hour());
        } else {
          this.onChange(value.valueOf());
        }
      }
    });

    this.subscriptions.push(subscription);
  }

  private setLocaleValueSubscription(): void {
    this.subscriptions.push(
      this.appFacade.currentLanguage$.pipe(filter(Boolean)).subscribe((language: Language) => {
        this.dateTimeAdapter.setLocale(LOCALE_CODE_LIST[language]);
      }),
    );
  }

  private initDateControlValidationSubscription(): void {
    if (this.ngControl) {
      this.dateControl.setValidators(this.ngControl.control?.validator);

      const subscription = this.ngControl.statusChanges.subscribe(() => {
        this.cd.markForCheck();
      });

      this.subscriptions.push(subscription);
    }
  }

  private setStartTimeForHourMode(): void {
    if (this.isHourOnlyMode) {
      this.startTime = moment().minute(0).second(0).millisecond(0);
    }
  }
}
